import routeMetadataManifestJson from './routeMetadataManifest.json'

type RouteMetadata = {
  title: string
  description: string
  canonical: string
  socialTitle: string
  socialDescription: string
  robots: string | null
  generatedHtml: boolean
  sitemap: boolean
}

const routeMetadataManifest = routeMetadataManifestJson as Record<string, RouteMetadata>

const NOT_FOUND_METADATA = {
  title: 'Page Not Found | Sugar Gremlin',
  description: 'The page you were looking for could not be found.',
  socialTitle: 'Page Not Found | Sugar Gremlin',
  socialDescription: 'The page you were looking for could not be found.',
  robots: 'noindex, nofollow, noarchive',
} as const

function setMeta(selector: string, attribute: 'name' | 'property', key: string, content: string) {
  let element = document.querySelector<HTMLMetaElement>(selector)
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, key)
    document.head.appendChild(element)
  }
  element.content = content
}

function removeMeta(selector: string) {
  document.querySelector(selector)?.remove()
}

function setCanonical(href: string) {
  let element = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!element) {
    element = document.createElement('link')
    element.rel = 'canonical'
    document.head.appendChild(element)
  }
  element.href = href
}

function removeCanonical() {
  document.querySelector('link[rel="canonical"]')?.remove()
}

function applyMetadata(metadata: RouteMetadata) {
  document.title = metadata.title
  setCanonical(metadata.canonical)
  setMeta('meta[name="description"]', 'name', 'description', metadata.description)
  setMeta('meta[property="og:title"]', 'property', 'og:title', metadata.socialTitle)
  setMeta('meta[property="og:description"]', 'property', 'og:description', metadata.socialDescription)
  setMeta('meta[property="og:url"]', 'property', 'og:url', metadata.canonical)
  setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', metadata.socialTitle)
  setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', metadata.socialDescription)
  if (metadata.robots) {
    setMeta('meta[name="robots"]', 'name', 'robots', metadata.robots)
  } else {
    removeMeta('meta[name="robots"]')
  }
}

function applyNotFoundMetadata() {
  document.title = NOT_FOUND_METADATA.title
  removeCanonical()
  setMeta('meta[name="description"]', 'name', 'description', NOT_FOUND_METADATA.description)
  setMeta('meta[property="og:title"]', 'property', 'og:title', NOT_FOUND_METADATA.socialTitle)
  setMeta('meta[property="og:description"]', 'property', 'og:description', NOT_FOUND_METADATA.socialDescription)
  removeMeta('meta[property="og:url"]')
  setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', NOT_FOUND_METADATA.socialTitle)
  setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', NOT_FOUND_METADATA.socialDescription)
  setMeta('meta[name="robots"]', 'name', 'robots', NOT_FOUND_METADATA.robots)
}

function normalizedPath(pathname: string) {
  const trimmed = pathname.replace(/\/+$/, '')
  return trimmed || '/'
}

export function routeMetadataForPath(pathname: string) {
  const path = normalizedPath(pathname)
  if (/^\/admin(?:\/|$)/.test(path)) return routeMetadataManifest['/admin'] ?? null
  return routeMetadataManifest[path] ?? null
}

export function applyRouteMetadata(pathname = window.location.pathname) {
  const metadata = routeMetadataForPath(pathname)
  if (metadata) {
    applyMetadata(metadata)
    return
  }
  applyNotFoundMetadata()
}
